import './App.css'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    Badge,
    DarkMode,
    Flex,
    HStack,
    IconButton,
    Modal,
    ModalContent,
    ModalOverlay,
    Spacer,
    Spinner,
    TabList,
    TabPanels,
    Tabs,
    Tooltip,
    useColorMode,
    useColorModeValue
} from '@chakra-ui/react'
import { createWs, fetchConfig, fetchSearch, getAllFixFields, getFixConfig, getUniqueToken, webGateway as backend } from './API'
import { ParsedMessageBlock } from './components/ParsedMessageTable/ParsedMessageBlock'
import { FIXMessageParseInputArea } from './components/FIXMessageParseInputArea/FIXMessageParseInputArea'
import { Loader, LoaderWrapper } from './components/Loader/Loader'
import { ChronicleFIXInfo } from './components/ChronicleFIX/ChronicleFIXInfo'
import { FIXMessagesSearchInput } from './components/ParsedMessageTable/FIXMessagesSearchInput'
import { Tab, TabPanel } from './components/Tabs/Tab'
import { defineIsSessionManager } from './utils'
import { SplitterValues } from './components/ParsedMessageTable/TableSplitterRow'
import { getSessionIdFromFixCfg, getSessionIdFromSessionIdObject } from '../fix-session-manager/components/utils'
import { Notifications } from '../fix-session-manager/components/Notifications/Notifications'
import { RoutingRules } from '../fix-session-manager/components/RoutingRules/RoutingRules'
import { rulesValidation } from '../fix-session-manager/components/RoutingRules/validation'
import { SessionManager } from '../fix-session-manager/components/SessionManager/SessionManager'
import { FiBell, FiMoon, FiSun } from 'react-icons/fi'
import { unwrap } from '../fix-session-manager/components/AddSessionForm/formUtils'

type Field = {
    name: string;
    tag: number;
    type: string;
};

type FieldManifest = {
    [key: string]: Field;
};

type FixVersionHandlerClassesMapping = {
    [key: string]: any;
};

type FieldSuggestion = {
    id: string;
    name: string;
    display: string;
    displayLowerCase: string;
    type: string;
    tag: number;
};

type FieldSuggestions = {
    [key: string]: FieldSuggestion;
};

interface IGetSearchResultsProps {
    transId: number
    curSessionId: string
    order: string
    fromSequenceNumber: number
    toSequenceNumber: number
    secondaryQuery?: string
    limit: number,
    filteredSessions: string[]
}

const defaultSearchParams: IGetSearchResultsProps = {
    transId: 0,
    curSessionId: '',
    order: 'DESCENDING',
    fromSequenceNumber: -1,
    toSequenceNumber: -1,
    limit: 50,
    filteredSessions: []
}

const tabPanelsStyle = { height: '100%' }
const searchPanelStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column'
}

const place = {
    ASCENDING: {
        up: 'current',
        down: 'next',
        upFirst: 'current',
        downLast: 'next'
    },
    DESCENDING: {
        up: 'next',
        down: 'current',
        upFirst: 'current',
        downLast: 'next'
    }
} as any

const sessionIsInvalid = {
    isActive: undefined,
    isConnected: undefined
}

function App() {
    const [token, setToken] = useState<string | undefined>(undefined)
    const [textareaValue, setTextareaValue] = useState('')
    const [response, setResponse]: any[] = useState(undefined)
    const [responseBuffer, setResponseBuffer]: any[] = useState([])
    const [dbSearchQuery, setDbSearchQuery] = useState('')
    const [selectedSeqNum, setSelectedSeqNum] = useState(0)
    const [parseError, setParseError] = useState(false)
    const [isSearch, setIsSearch] = useState<boolean | undefined>(undefined)
    const [isConnected, setIsConnected] = useState<boolean>(false)
    const [sampleCounter, setSampleCounter] = useState(0)
    const [searchParams, setSearchParams] = useState(defaultSearchParams)
    const [sessionsInfo, setSessionsInfo] = useState([])
    const [currentTab, setCurrentTab] = useState(0)
    const [offeredSearchRequest, offerSearchRequest] = useState<any>(undefined)
    const [insertMode, setInsertMode] = useState(false)
    const [searchError, setSearchError] = useState('')
    const [notifications, setNotifications] = useState<any[]>([])
    const [config, setConfig]: any = useState({})
    const [searchMessageReceived, setSearchMessageReceived] = useState(true)
    const [insertData, setInsertData] = useState<{ seqNum?: number; direction: SplitterValues }>({
        seqNum: undefined,
        direction: SplitterValues.Up
    })
    const [rules, setRules] = useState<any[]>([])
    const [fieldSuggestions, setFieldSuggestions] = useState<FieldSuggestions>({})
    const [fieldSuggestionsWithMetadata, setFieldSuggestionsWithMetadata] = useState<FieldSuggestions>({})
    const [notificationFilterBySessionId, setNotificationFilterBySessionId] = useState<string>()
    const [demoStatus, setDemoStatus] = useState('NON_AVAILABLE')

    const insertModeRef = useRef(insertMode)

    useEffect(() => {
        insertModeRef.current = insertMode
    }, [insertMode])

    const responseBufferRef = useRef(responseBuffer)

    useEffect(() => {
        responseBufferRef.current = responseBuffer
    }, [responseBuffer])

    const insertDataRef = useRef(insertData)

    useEffect(() => {
        insertDataRef.current = insertData
    }, [insertData])

    const searchParamsRef = useRef(searchParams)

    useEffect(() => {
        searchParamsRef.current = searchParams
    }, [searchParams])

    const handleSearch = useCallback(
        (searchRequest: any) => {
            if (!isSearch) return

            offerSearchRequest(undefined)
            setSearchError('')
            setSearchMessageReceived(false)
            setSearchParams((prevState) => ({
                ...defaultSearchParams,
                order: searchParams.order,
                transId: prevState.transId,
                curSessionId: prevState.curSessionId,
                ...searchRequest
            }))

            getSearchResults({ ...searchRequest })
        },
        [isSearch, searchParams]
    )

    const sortBySessionID = (sessionA: any, sessionB: any) => {
        if (sessionA.sessionID < sessionB.sessionID) {
            return -1;
        }
        if (sessionA.sessionID > sessionB.sessionID) {
            return 1;
        }
    
        return 0;
    }

    const notificationId = useRef(0)

    const notify = (notification: any) => {
        notification.id = notificationId.current++
        setNotifications((oldNotifications) => ([...oldNotifications, notification]))
    }

    const notificationsHasErrors = notifications.some(notification => notification.level === 'ERROR')

    const handleSocketMessage = async (msg: any) => {
        // console.log(!msg.text)
        if (!msg.text) {
            console.log(msg)
            return
        }
        const res = await msg.text()
        try {
            const json = JSON.parse(res)

            switch (json['@ResponseItem'].key) {
                case 'searchResponse':
                    setSearchMessageReceived(true)
                    if (json['@ResponseItem'].payload.transactionId !== searchParams?.transId?.toString()) {
                        return
                    }
                    let response = json['@ResponseItem'].payload.response

                    if (insertModeRef.current) {
                        response = response.slice(0, -1)
                        setResponse((prevState: any[]) => {
                            const insertPlace = place[searchParamsRef.current.order][insertDataRef.current.direction]

                            let index = prevState.indexOf(
                                prevState.find((el) => el.seqNumber === insertDataRef.current.seqNum)
                            )

                            if (index === -1) {
                                index = insertDataRef.current.direction === 'upFirst' ? 0 : prevState.length - 1
                            }
                            const newData = [...prevState]
                            const insertIndex = insertPlace === 'next' ? index + 1 : index
                            const gapIndex = insertPlace === 'next' ? index : index - 1
                            if(response.length) {
                                if (isInvertOrderNeeded(searchParamsRef.current.order, insertDataRef.current.direction)) {
                                    response.reverse()
                                }

                                if (insertDataRef.current.direction === 'up') {
                                    newData[gapIndex]._noGapAfter = true
                                }

                                response.forEach((item: any, index: number) => {
                                    item._inserted = true
                                    if (index !== response.length - 1
                                        || insertDataRef.current.direction === 'down'
                                        || insertDataRef.current.direction === 'upFirst') {
                                        item._noGapAfter = true
                                    }
                                })
                            } else {
                                const item = newData[gapIndex]
                                if (item) {
                                    const lastIndex = newData.length - 1
                                    if (gapIndex === lastIndex) {
                                        if (newData[lastIndex]._noResultsAfter === undefined) {
                                            newData[lastIndex]._noResultsAfter = 0
                                        }
                                        newData[lastIndex]._noResultsAfter++
                                    } else {
                                        item._noGapAfter = true
                                    }
                                } else if (gapIndex === -1) {
                                    if (newData[0]._noResultsBefore === undefined) {
                                        newData[0]._noResultsBefore = 0
                                    }
                                    newData[0]._noResultsBefore++
                                }
                            }
                            newData.splice(insertIndex, 0, ...response)
                            return newData
                        })
                    } else {
                        if (response[response.length - 1] === 'endOfStream') {
                            response = response.slice(0, -1)
                            const newResponse = [...responseBufferRef.current, ...response]
                            setResponse(newResponse)
                            setSelectedSeqNum(newResponse[0]?.seqNumber)
                            setResponseBuffer([])
                            const dbSearchQuery = json['@ResponseItem'].payload.searchQuery
                            setDbSearchQuery(dbSearchQuery)
                        } else if (response[0]?.error) {
                            setSearchError(response[0]?.error)
                        } else {
                            setSearchError('')
                            setResponseBuffer((prevState: any[]) => [...prevState, ...response] as any)
                        }
                    }

                    break
                case 'sessionState':
                case 'SessionInfo':
                    const updateFromFixEngine = json['@ResponseItem'].key === 'SessionInfo'
                    const update = updateFromFixEngine
                        ? json['@ResponseItem'].payload['@SessionInfo']
                        : json['@ResponseItem'].payload
                    setSessionsInfo((sessionsInfo: any) => {
                        return sessionsInfo.map((session: any) => {
                            const sessionUpdates = updateFromFixEngine 
                                ? update.sessions
                                    .map((wrappedSession: any) => {
                                        const s = { ...wrappedSession['@SessionDetails'] }
                                        s.sessionID = s.sessionID['@SessionID']
                                        s.isActive = s.activated
                                        s.activated = undefined
                                        s.isConnected = s.connected
                                        s.connected = undefined
                                        return s
                                    })
                                // Remove this, as soon as Chronicle Wire is fixed
                                : Array.isArray(update.sessionStates) ? update.sessionStates : [] 

                            const updatedSession = sessionUpdates.find(
                                (el: any) => session.sessionID === getSessionIdFromSessionIdObject(el.sessionID)
                            )

                            if (updatedSession) {
                                return {
                                    ...updatedSession,
                                    params: session.params,
                                    sessionID: getSessionIdFromSessionIdObject(updatedSession.sessionID)
                                }
                            } else if (!update.partialUpdate) {
                                // This means, that sessionCfg is present in 'fix-config',
                                // but is absent in FixEngine.
                                // Usually that means, that session was misconfigured and was removed
                                // by FixEngine as faulty
                                return {
                                    ...session,
                                    ...sessionIsInvalid
                                }
                            }

                            return session
                        })
                        .sort(sortBySessionID)
                    })
                    break
                case 'fixConfig':
                    const routingRules = json['@ResponseItem'].payload.routingRules
                    setRules(routingRules)
                    setSessionsInfo((sessionsInfo) => {
                        const data = json['@ResponseItem'].payload.fixSessionCfg
                        // Remove this, as soon as Chronicle Wire is fixed
                        if (!Object.keys(data).length) {
                            return [];
                        }

                        const sessions = data.map((session: any) => ({
                            id: getSessionIdFromFixCfg(session),
                            params: session
                        }))
                        return sessions.map(({ id, params }: { id: any; params: any }) => {
                            const currentSession = sessionsInfo.find((session: any) => session.sessionID === id)
                            return currentSession
                                ? // @ts-ignore
                                  { ...currentSession, params: params }
                                : {
                                      sessionID: id,
                                      new: true,
                                      params: params
                                  }
                        })
                        .sort(sortBySessionID)
                    })
                    break
                case 'logMsg':
                    const logMsg = json['@ResponseItem'].payload
                    const type = Object.keys(logMsg)[0]
                    const logMsgBody = logMsg[type]
                    logMsgBody.level = (type.endsWith('WarnMsg') ? 'WARN' : type.endsWith('ErrorMsg') ? 'ERROR' : 'INFO')
                    notify(logMsgBody)
                    break
                case 'routingRuleValidationResult':
                    const validationResult = json['@ResponseItem'].payload
                    const validation: any = (rulesValidation as any)[validationResult.ruleId]

                    if (validation && validation.transactionId === validationResult.transactionId) {
                        validation.resolve(validationResult)
                    }
                    break
                case 'demoStatus':
                    const newStatus = unwrap(json['@ResponseItem'].payload)
                    // const newStatus = unwrap(json['@ResponseItem'].payload['@DemoStatusUpdate'].status)
                    setDemoStatus((status) => {
                        if (newStatus !== 'IN_PROGRESS' || status === 'AVAILABLE') {
                            return newStatus
                        }
                        return status
                    })
                    
                    break    
                case 'bootstrap':
                    // 'bootstrap' message is expected at the server startup.
                    // Do nothing.
                    break
                default:
                    notify({
                        level: 'WARN',
                        message: 'Unhandled message type',
                        ...json['@ResponseItem']
                    })
            }
        } catch (e: any) {
            notify({
                level: 'ERROR',
                message: `Failed to handle socket message: ${e?.message}`,
                stackTrace: e?.stack,
                socketMessage: res
            })
        }
    }

    const getSearchResults = (props: Partial<IGetSearchResultsProps>) => {
        const params = { ...searchParams, ...props } as IGetSearchResultsProps
        if (params.fromSequenceNumber !== -1 || params.toSequenceNumber !== -1) {
            setInsertMode(true)
        } else {
            setInsertMode(false)
        }
        setSearchParams((prevState) => ({ ...prevState, transId: prevState.transId && prevState.transId + 1 }))
        return fetchSearch(params)
    }

    const isInvertOrderNeeded = (order: string, direction: string) => (direction === 'down' || direction === 'upFirst')

    const invertOrder = (order: string) => (order === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING')

    const handleDividerClick = useCallback((direction: SplitterValues, seqNum: number, deltaSeqNum: number) => {
        setInsertData({ seqNum, direction })

        const fromSequenceNumber =
            direction === SplitterValues.Up
                ? (deltaSeqNum === -1 ? -1 : deltaSeqNum + 1) :
            direction === SplitterValues.UpFirst || direction === SplitterValues.DownLast 
                ? seqNum :
            direction === SplitterValues.Down
                ? (seqNum === -1 ? -1 : seqNum + 1)
                : undefined

        const toSequenceNumber =
            direction === SplitterValues.Up
                ? (seqNum === -1 ? -1 : seqNum - 1) :
            direction === SplitterValues.UpFirst || direction === SplitterValues.DownLast
                ? deltaSeqNum :
            direction === SplitterValues.Down
                ? (deltaSeqNum === -1 ? -1 : deltaSeqNum - 1)
                : undefined

        const order = isInvertOrderNeeded(searchParams.order, direction) ? invertOrder(searchParams.order) : searchParams.order

        getSearchResults({
            ...defaultSearchParams,
            curSessionId: searchParams.curSessionId,
            order,
            fromSequenceNumber,
            toSequenceNumber,
            secondaryQuery: "",
            limit: 5
        })
    }, [searchParams])

    const handleSend = (value = textareaValue) => {
        backend
            .post(
                '/sn/fixparser',
                new URLSearchParams({
                    'raw-fix': value
                })
            )
            .then((res: any) => {
                if (res.data) {
                    setResponse(res.data)
                    setParseError(false)
                }
            })
            .catch(() => {
                setResponse([])
                setParseError(true)
            })
    }

    const handleShowSample = () => {
        backend.get(`/sn/fixsample?count=${sampleCounter}`).then((res: any) => {
            setTextareaValue(res.data)
            setSelectedSeqNum(0)
            handleSend(res.data)
        })
        setSampleCounter((prev) => prev + 1)
    }

    const handleSocketOpen = (ws: any, id: any) => {
        // @ts-ignore
        setIsConnected(true)
        ws.send(`{ sessionId: ${id} }`)
        getFixConfig().then(() => {})
    }

    const handleShowSearch = (searchRequest?: any) => {
        setCurrentTab(config.router ? 2 : 1)
        if (searchRequest) {
            offerSearchRequest(searchRequest)
        }
    }

    useEffect(() => {
        if (isSearch !== undefined) {
            document.title = isSearch ? 'Chronicle FIX' : 'Chronicle FIX Parser'
        }
    }, [isSearch])

    // console.log(demoStatus)

    useEffect(() => {
        fetchConfig()
            .then((res) => {
                setIsSearch(true)
                const config = res.data['@UiCfg']
                console.log(config)
                setConfig(config)
                const status = unwrap(config.demoStatus)
                console.log('consig.demoStatus', status)
                setDemoStatus(status)
            })
            .catch((err) => {
                console.log(err)
                console.log('isSearch manual set')
                setIsSearch(defineIsSessionManager())
            })
        getUniqueToken().then((res: any) => {
            setSearchParams((prevState) => ({ ...prevState, curSessionId: res.data }))        
            setToken(res.data)
            createWs({
                handleMessage: handleSocketMessage,
                onOpen: (ws: any) => handleSocketOpen(ws, res.data),
                onClose: (e: any) => {
                    setIsConnected(false)
                }
            })
        })
        getAllFixFields().then((res) => {
            const fieldSuggestionsWithMetadata: FieldSuggestions = {}
            Object.entries(res.data as FieldManifest)
                .filter(([, field]) => !field.name.startsWith("_"))
                .forEach(([key, field]) => 
                    fieldSuggestionsWithMetadata[key] = {
                        id: key,
                        name: field.name,
                        display: `${field.name} <${field.tag < 0 ? 'metadata' : field.tag}>`,
                        displayLowerCase: `${key} <${field.tag < 0 ? 'metadata' : field.tag}>`,
                        type: field.type,
                        tag: field.tag
                    }
                )

            setFieldSuggestionsWithMetadata(fieldSuggestionsWithMetadata)

            const fieldSuggestionsWithoutMetadata = Object.fromEntries(
                Object.entries(fieldSuggestionsWithMetadata)
                    .filter(([, suggestion]) => suggestion.tag >= 0)
            )
            setFieldSuggestions(fieldSuggestionsWithoutMetadata)
        })

        if (!isSearch) {
            const urlSearchParams = new URLSearchParams(window.location.search)
            const params = Object.fromEntries(urlSearchParams.entries())
            if (params.token) {
                backend.get(`/sn/load?token=${params.token}`).then((res: any) => {
                    const parts = /(?:[\s\S]*)"raw-fix":"([^"]*)"(?:[\s\S])*/gm.exec(res.data)
                    const replaced = res.data.replace(parts && parts[1], ' ') //.replace(/\n/gm, '\\n')
                    res = JSON.parse(replaced)
                    setTextareaValue((parts && parts[1]) || '')
                    setSelectedSeqNum(Number(res['highlighted-row']))
                    setResponse(res['as-json'])
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleCurrentTab = (tab: number | string) => {
        if (typeof tab === "number") {
            setCurrentTab(tab)
        } else {

        }
    }

    useEffect(() => {
        const parts = window.location.href.split('#')
        const path = parts[parts.length-1]
        switch (path) {
            case 'routing-rules':
                if (config.router) {
                    setCurrentTab(1)
                }
                break
            case 'search':
                setCurrentTab(config.router ? 2 : 1)
                break
            default:
                break
        }
    }, [window.location.href, config])

    const onSortChange = useCallback((val: string) => {
        setSearchParams((prevState) => ({ ...prevState, order: val }))
        getSearchResults({ order: val })
    }, [searchParams])

    const tabsShadow = useColorModeValue('2xl', 'dark-lg')
    const { colorMode, toggleColorMode } = useColorMode()

    const showError = (sessionID: string) => {
        setCurrentTab(config.router ? 3 : 2)
        setNotificationFilterBySessionId(sessionID)
    }

    const onClearAllErrors = useCallback(() => {
        setCurrentTab(0)
    }, [])

    return (
        <>
        {isSearch === undefined &&
                        <LoaderWrapper>
                            <Loader />
                        </LoaderWrapper>}
        <div 
            className="App"
            style={{
                ...(isSearch && { overflow: 'hidden' }),
                backgroundColor: 'var(--chakra-colors-background)'
            }}
         >
                <div
                    className="App-content"
                    style={
                        isSearch
                        ? {
                            height: '100vh',
                            overflow: 'hidden'
                        }
                        : {
                            minHeight: '50vh',
                        }
                    }
                >
                    {isSearch !== undefined && (
                        <>
                            {isSearch ? (
                                <>
                                    <Modal isCentered isOpen={!isConnected} onClose={() => {}}>
                                        <ModalOverlay />
                                        <ModalContent background='none' boxShadow='none'>
                                            <Flex style={{ justifyContent: 'center' }}>
                                                <Spinner size='xl' thickness='5px' />
                                            </Flex>
                                        </ModalContent>
                                    </Modal>
                                    <Tabs
                                        index={currentTab}
                                        onChange={setCurrentTab}
                                        height='100vh'
                                        // paddingTop='131px'
                                        paddingTop='71px'
                                        // variant='enclosed'
                                    >
                                        <Flex
                                            paddingBottom='2px'
                                            shadow={tabsShadow}
                                            justifyContent='space-around'
                                            position='absolute'
                                            width='100%'
                                            top={0}
                                            zIndex={4}
                                            backgroundColor='#181f4c'
                                        >
                                        <DarkMode>
                                        <TabList
                                            borderBottom='none'
                                            maxWidth='var(--base-width)'
                                            width='100%'
                                            paddingLeft={6}
                                            paddingRight={6}
                                        >
                                            <img
                                                style={{
                                                    maxWidth: '110px',
                                                    margin: '20px 20px 20px 0'
                                                }}
                                                alt="Chronicle Software"
                                                src="./logo.svg"
                                            />
                                            {!config.searchOnly &&
                                                <Tab>Sessions</Tab>
                                            }
                                            {!config.searchOnly && config.router &&
                                                <Tab>Routing Rules</Tab>
                                            }
                                            <Tab>Search</Tab>
                                            <Spacer/>
                                            <Flex
                                                justify='center'
                                                align='center'
                                                paddingRight={notifications.length !== 0 ? '0.5rem' : undefined}
                                            >
                                                <Tooltip
                                                    label={`Switch to ${colorMode === 'dark' ? 'light' : 'dark'} mode`}
                                                >
                                                    <IconButton
                                                        isRound
                                                        aria-label='dark-light-mode-switch'
                                                        icon={colorMode === 'dark' ? <FiSun /> : <FiMoon />}
                                                        variant='ghost'
                                                        onClick={toggleColorMode}
                                                    />
                                                </Tooltip>
                                            </Flex>
                                            
                                            {notifications.length !== 0 &&
                                                <Tab 
                                                    {...notificationsHasErrors && {
                                                        backgroundColor: 'red.700',
                                                        _selected: {
                                                            background: 'red.900',
                                                        }
                                                    }}
                                                    paddingInlineStart={5}
                                                    paddingInlineEnd={5}
                                                >
                                                    <HStack>
                                                        <FiBell /> <Badge variant='solid' colorScheme='red' >
                                                            {notifications.length}
                                                        </Badge>
                                                    </HStack>
                                                </Tab>
                                            }
                                        </TabList>
                                        </DarkMode>
                                        </Flex>
                                        <TabPanels 
                                            style={{
                                                ...tabPanelsStyle,
                                                height: '100%',
                                                display: 'block',
                                                overflow: 'auto',
                                            }}
                                        >
                                            {!config.searchOnly &&
                                                <TabPanel>
                                                    {currentTab === 0 &&
                                                    <SessionManager
                                                        sessions={sessionsInfo}
                                                        errors={notifications}
                                                        showError={showError}
                                                        routingRules={rules}
                                                        handleShowSearch={handleShowSearch}
                                                        config={config}
                                                        notify={notify}
                                                        demoStatus={demoStatus}
                                                    />}
                                                </TabPanel>
                                            }
                                            {!config.searchOnly && config.router &&
                                                <TabPanel>
                                                    <RoutingRules
                                                        // @ts-ignore
                                                        fieldSuggestions={fieldSuggestions}
                                                        token={token}
                                                        rules={rules}
                                                        setRules={setRules}
                                                        routes={sessionsInfo
                                                            .filter((session: any) => session.params.routingDestination)
                                                            .map((session: any) => session.sessionID)
                                                        }
                                                        policyAllEnabled={config.router['@UiRouterCfg'].policyAllEnabled}
                                                    />
                                                </TabPanel>
                                            }
                                            <TabPanel
                                                sx={searchPanelStyle}
                                                paddingX={0}
                                            >
                                                <FIXMessagesSearchInput
                                                    // @ts-ignore
                                                    fieldSuggestions={fieldSuggestionsWithMetadata}
                                                    offeredSearchRequest={offeredSearchRequest}
                                                    searchInProgress={!searchMessageReceived}
                                                    handleSearch={handleSearch}
                                                    sessions={sessionsInfo.map((session: any) => session.sessionID)}
                                                    dbQuery={dbSearchQuery}
                                                    searchResponse={response}
                                                    showSessionIdSelector={!config.searchOnly}
                                                />
                                                <ParsedMessageBlock
                                                    isSearch={true}
                                                    selectedSeqNum={selectedSeqNum}
                                                    onSortChange={onSortChange}
                                                    handleDividerClick={handleDividerClick}
                                                    searchParams={searchParams}
                                                    handleSearchChange={offerSearchRequest}
                                                    onSelectedSeqNumChange={setSelectedSeqNum}
                                                    data={response}
                                                    error={searchError}
                                                />
                                            </TabPanel>
                                            {notifications.length !== 0 &&
                                                <TabPanel>
                                                    <Notifications
                                                        // @ts-ignore
                                                        notifications={notifications}
                                                        setNotifications={setNotifications}
                                                        onClearAll={onClearAllErrors}
                                                        notificationFilterBySessionId={notificationFilterBySessionId}
                                                        setNotificationFilterBySessionId={setNotificationFilterBySessionId}
                                                    />
                                                </TabPanel>
                                            }
                                        </TabPanels>
                                    </Tabs>
                                </>
                            ) : (<>
                                <FIXMessageParseInputArea
                                    handleSend={handleSend}
                                    handleShowSample={handleShowSample}
                                    parseError={parseError}
                                    selectedSeqNum={selectedSeqNum}
                                    setTextareaValue={setTextareaValue}
                                    textareaValue={textareaValue}
                                />
                                <ParsedMessageBlock
                                    isSearch={false}
                                    selectedSeqNum={selectedSeqNum}
                                    onSortChange={onSortChange}
                                    handleDividerClick={handleDividerClick}
                                    handleSearchChange={handleSearch}
                                    onSelectedSeqNumChange={setSelectedSeqNum}
                                    data={response}
                                    error={searchError}
                                />
                                <ChronicleFIXInfo />
                            </>)}
                        </>
                    )}
                </div>
        </div>
        </>
    )
}

export default App
